import { useMemo } from 'react';
import { Alert, SimpleGrid, Stack } from '@mantine/core';
import type { ComponentProjectMetricsResponse } from '../../../../types/dashboard';
import { CompanyCertificationCard } from '../../arch/cards/CompanyCertificationCard';
import { mapArchMetricsData, hasArchCompanyMetricsData, isArchCompanyMetric } from '../../arch/mapArchMetricsData';
import { ComponentProjectMetricChartCard } from '../ComponentProjectMetricChartCard';

type ArchComponentProjectMetricsProps = {
  data: ComponentProjectMetricsResponse;
};

export function ArchComponentProjectMetrics({ data }: ArchComponentProjectMetricsProps) {
  const archData = useMemo(() => mapArchMetricsData(data), [data]);

  const otherMetrics = useMemo(() => {
    if (!data?.sections?.length) return [];
    return data.sections.flatMap((section) =>
      section.metrics
        .filter((metric) => !isArchCompanyMetric(metric.metricId, metric))
        .map((metric) => ({
          key: `${section.sectionId}-${metric.metricId}`,
          metric,
          sectionTitle: section.title,
        }))
    );
  }, [data]);

  const showCompanyMetrics = archData !== null && hasArchCompanyMetricsData(archData);

  if (!showCompanyMetrics && otherMetrics.length === 0) {
    return (
      <Alert color="blue" variant="light">
        No ARCH component project metrics are available yet.
      </Alert>
    );
  }

  return (
    <Stack spacing="lg">
      {showCompanyMetrics && archData ? (
        <SimpleGrid cols={2} spacing="lg" breakpoints={[{ maxWidth: 1200, cols: 1 }]}>
          <CompanyCertificationCard data={archData} showStatus={false} />
        </SimpleGrid>
      ) : null}
      {otherMetrics.length > 0 ? (
        <SimpleGrid
          cols={2}
          spacing="lg"
          breakpoints={[{ maxWidth: 1200, cols: 1 }]}
          sx={{ alignItems: 'stretch' }}
        >
          {otherMetrics.map(({ key, metric, sectionTitle }) => (
            <ComponentProjectMetricChartCard
              key={key}
              metric={metric}
              sectionTitle={sectionTitle}
            />
          ))}
        </SimpleGrid>
      ) : null}
    </Stack>
  );
}
